import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '@/lib/utils';

export function formatContributionAmount(amount, localeFmt = 'fr-FR') {
  return `${Number(amount || 0).toLocaleString(localeFmt)} FBU`;
}

export function memberInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

/** Badge statut SaaS : Payé / En attente / Rejeté / Dû / En retard */
export function ContributionStatusBadge({ status, dueState, className = '' }) {
  const { t } = useTranslation('common');
  if (dueState === 'overdue') {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-semibold text-red-800',
          className
        )}
      >
        {t('contributionsPage.dueSection.overdue')}
      </span>
    );
  }
  if (dueState === 'due' || status === 'unpaid') {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-900',
          className
        )}
      >
        {dueState === 'due'
          ? t('contributionsPage.dueSection.due')
          : t('contributionsPage.table.statusPendingShort')}
      </span>
    );
  }
  const st = String(status || '').toLowerCase();
  if (st === 'completed' || st === 'paid' || st === 'approved') {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800',
          className
        )}
      >
        {t('contributionsPage.table.statusPaidShort')}
      </span>
    );
  }
  if (st === 'rejected') {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-semibold text-red-800',
          className
        )}
      >
        {t('contributionsPage.table.statusRejectedShort')}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700',
        className
      )}
    >
      {t('contributionsPage.table.statusPendingShort')}
    </span>
  );
}

/** Affiche N°00045 (padding numérique) pour un repère rapide. */
export function formatMemberNumberLabel(memberNumber) {
  const raw = String(memberNumber || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/^N[°º.]?\s*/i, '');
  if (/^\d+$/.test(digits)) return `N°${digits.padStart(5, '0')}`;
  return raw.startsWith('N') ? raw : `N°${raw}`;
}

/**
 * Cellule membre hiérarchisée : nom → N° → fonction (ex. Président).
 * Le nom est le signal principal pour retrouver rapidement la ligne.
 */
export function ContributionMemberCell({
  name,
  memberNumber,
  title,
  photoUrl,
  className = '',
}) {
  const label = String(name || '').trim() || '—';
  const numberLabel = formatMemberNumberLabel(memberNumber);
  const roleLabel = String(title || '').trim();
  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
        />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e8eef5] text-[11px] font-bold tracking-wide text-[#1F4668] ring-1 ring-slate-200/80">
          {memberInitials(label)}
        </div>
      )}
      <div className="min-w-0 leading-snug">
        <p className="truncate text-[15px] font-bold tracking-tight text-slate-900">{label}</p>
        {numberLabel ? (
          <p className="mt-0.5 truncate font-mono text-[11px] font-semibold tracking-wide text-[#1F4668]/85">
            {numberLabel}
          </p>
        ) : null}
        {roleLabel ? (
          <p className="mt-0.5 truncate text-[11px] font-medium text-slate-500">{roleLabel}</p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Menu « … » : détail, reçu, avis, modifier, approuver, supprimer, etc.
 * Ne rend que les actions fournies (permissions côté parent).
 */
export function ContributionRowActionsMenu({
  onDetail,
  onReceipt,
  onInvoice,
  onEdit,
  onApprove,
  onDelete,
  onRecordPayment,
  onComptabiliser,
  comptabilising = false,
}) {
  const { t } = useTranslation('common');
  const items = [
    onDetail && { key: 'detail', label: t('contributionsPage.actions.detail'), onClick: onDetail },
    onReceipt && { key: 'receipt', label: t('contributionsPage.receipt.open'), onClick: onReceipt },
    onInvoice && { key: 'invoice', label: t('contributionsPage.invoice.open'), onClick: onInvoice },
    onRecordPayment && {
      key: 'record',
      label: t('contributionsPage.dueSection.record'),
      onClick: onRecordPayment,
    },
    onApprove && { key: 'approve', label: t('contributionsPage.mobile.approve'), onClick: onApprove },
    onComptabiliser && {
      key: 'compta',
      label: comptabilising
        ? t('contributionsPage.mobile.comptabilising')
        : t('contributionsPage.mobile.comptabiliser'),
      onClick: onComptabiliser,
      disabled: comptabilising,
    },
    onEdit && { key: 'edit', label: t('contributionsPage.actions.edit'), onClick: onEdit },
  ].filter(Boolean);

  if (!items.length && !onDelete) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]/40"
          aria-label={t('contributionsPage.actions.menu')}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[11rem]">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.key}
            disabled={it.disabled}
            onClick={(e) => {
              e.stopPropagation();
              it.onClick?.(e);
            }}
          >
            {it.label}
          </DropdownMenuItem>
        ))}
        {onDelete ? (
          <>
            {items.length > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              className="text-red-700 focus:text-red-800"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(e);
              }}
            >
              {t('contributionsPage.actions.delete')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ContributionPaymentsKpiGrid({ items = [], loading = false }) {
  return (
    <>
      <div className="-mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1 touch-pan-x [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
        {items.map((it) => (
          <div
            key={it.key}
            className={cn(
              'max-w-[44vw] min-w-[9.5rem] shrink-0 snap-start rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm',
              it.accentClass
            )}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{it.label}</p>
            {loading ? (
              <Skeleton className="mt-2 h-6 w-20" />
            ) : (
              <p className="mt-1 text-base font-bold tabular-nums tracking-tight text-[#0f3350]">
                {it.value}
                {it.suffix ? (
                  <span className="ml-1 text-[10px] font-semibold text-slate-500">{it.suffix}</span>
                ) : null}
              </p>
            )}
            {it.sub ? <p className="mt-1 line-clamp-2 text-[10px] text-slate-500">{it.sub}</p> : null}
          </div>
        ))}
      </div>

      <div className="hidden gap-3 md:grid md:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => (
          <div
            key={it.key}
            className={cn(
              'rounded-xl border border-slate-200/90 bg-white px-4 py-3.5 shadow-sm',
              it.accentClass
            )}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{it.label}</p>
            {loading ? (
              <Skeleton className="mt-2 h-7 w-28" />
            ) : (
              <p className="mt-1.5 text-xl font-bold tabular-nums tracking-tight text-[#0f3350]">
                {it.value}
                {it.suffix ? (
                  <span className="ml-1 text-xs font-semibold text-slate-500">{it.suffix}</span>
                ) : null}
              </p>
            )}
            {it.sub ? <p className="mt-1 text-xs text-slate-500">{it.sub}</p> : null}
          </div>
        ))}
      </div>
    </>
  );
}

export function ContributionActiveFilterBadges({ badges = [], onClearAll }) {
  const { t } = useTranslation('common');
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {badges.map((b) => (
        <button
          key={b.key}
          type="button"
          onClick={b.onRemove}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
        >
          <span className="text-slate-500">{b.label}:</span>
          <span className="font-semibold text-slate-900">{b.value}</span>
          <span className="ml-0.5 text-slate-400" aria-hidden>
            ×
          </span>
        </button>
      ))}
      {typeof onClearAll === 'function' && badges.length > 1 ? (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-semibold text-[#2f6fd4] hover:text-[#0f3350]"
        >
          {t('contributionsPage.filters.clearAll')}
        </button>
      ) : null}
    </div>
  );
}

export function ContributionListSkeleton() {
  return (
    <div className="space-y-3">
      <div className="hidden gap-3 md:grid md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-7 w-32" />
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <Skeleton className="h-9 w-full" />
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContributionListEmpty({ title, hint }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center shadow-sm">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400 ring-1 ring-slate-200">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {hint ? <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export function ContributionListError({ message, onRetry }) {
  const { t } = useTranslation('common');
  return (
    <div className="rounded-xl border border-red-200 bg-red-50/80 px-6 py-10 text-center shadow-sm">
      <p className="text-sm font-semibold text-red-900">{message}</p>
      {typeof onRetry === 'function' ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex min-h-[40px] items-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-800 shadow-sm hover:bg-red-50"
        >
          {t('contributionsPage.errors.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function contributionReference(contrib) {
  const id = String(contrib?.id || '').trim();
  if (!id) return '—';
  if (id.startsWith('arrears:')) return id.replace(/^arrears:[^:]+:/, 'AVI-');
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}
